import { useState } from 'react';
import type { LandingContent } from '../content';
import { Reveal } from '../components/Reveal';
import { SectionContainer } from '../components/SectionContainer';
import { cn } from '../utils';

type FaqSectionProps = {
  content: LandingContent['faq'];
};

function renderAnswerWithBold(answer: string) {
  const boldPattern = /\*\*(.*?)\*\*/g;
  const lines = answer.split('\n');

  return lines.map((line, lineIndex) => {
    const segments: Array<{ text: string; bold: boolean }> = [];
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = boldPattern.exec(line)) !== null) {
      if (match.index > cursor) {
        segments.push({ text: line.slice(cursor, match.index), bold: false });
      }
      segments.push({ text: match[1] ?? '', bold: true });
      cursor = match.index + match[0].length;
    }

    if (cursor < line.length) {
      segments.push({ text: line.slice(cursor), bold: false });
    }

    if (segments.length === 0) {
      segments.push({ text: line, bold: false });
    }

    return (
      <span key={`line-${lineIndex}`}>
        {segments.map((segment, segmentIndex) =>
          segment.bold ? (
            <strong key={`segment-${lineIndex}-${segmentIndex}`} className="font-semibold text-[#2A3042]">
              {segment.text}
            </strong>
          ) : (
            <span key={`segment-${lineIndex}-${segmentIndex}`}>{segment.text}</span>
          )
        )}
        {lineIndex < lines.length - 1 ? <br /> : null}
      </span>
    );
  });
}

export function FaqSection({ content }: FaqSectionProps) {
  const [openId, setOpenId] = useState<string | null>(content.items[0]?.id ?? null);

  return (
    <section className="bg-white py-24 text-[#1C1C1E] sm:py-28">
      <SectionContainer className="max-w-[1410px]">
        <div className="mx-auto max-w-[1280px]">
          <Reveal variant="up">
            <h2 className="font-pangaia text-[32px] font-bold leading-[40px] sm:text-[54px] sm:leading-[1.05]">{content.title}</h2>
          </Reveal>
        </div>

        <div className="mx-auto mt-10 max-w-[1280px] space-y-3">
          {content.items.map((item, index) => {
            const isOpen = openId === item.id;
            return (
              <Reveal key={item.id} delay={index * 70} variant="up" durationMs={520}>
                <article className="overflow-hidden rounded-[8px] bg-[#F8F9FC] transition-[background-color,box-shadow] duration-300 hover:bg-[#FBFCFF] hover:shadow-[0_10px_24px_rgba(17,19,26,0.04)]">
                  <button
                    type="button"
                    onClick={() => setOpenId((prev) => (prev === item.id ? null : item.id))}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="font-pangaia text-[22px] leading-[28px]">{item.question}</span>
                    <span
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center text-[#1C1C1E] transition-transform duration-300',
                        isOpen ? 'rotate-45' : 'rotate-0'
                      )}
                      aria-hidden="true"
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M16 7v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M7 16h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={cn(
                      'grid transition-[grid-template-rows] duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    )}
                  >
                    <div className="overflow-hidden">
                      <p
                        className={cn(
                          'px-6 pb-6 font-pangram text-[16px] leading-[26px] text-[#61687C] transition-[opacity,transform] duration-300',
                          isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                        )}
                      >
                        {renderAnswerWithBold(item.answer)}
                      </p>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </SectionContainer>
    </section>
  );
}
